import type { ReactNode } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventSafetyQuery, useGetReportSafetyQuery } from "@api/pApi";
import { fmtNum } from "@lib/format";
import type { SafetyDayOut } from "@api/types";

/**
 * HSE panel — the event-level LTI rollup (StatBand + daily HSE trail) plus the
 * selected report's daily HSE header (well control, compliance checks, people &
 * incidents). Safety is a DAILY HEADER: no discrete open→close action list.
 * Every counter is server-computed by Oxbo-P; the FE only formats and lays out.
 */
export function HsePanel({ eventId, reportId }: { eventId: string; reportId: string }) {
  const { t, locale } = useLocale();

  const {
    data: rollup,
    isLoading: rollupLoading,
    isError: rollupError,
    refetch,
  } = useGetEventSafetyQuery(eventId, { skip: !eventId });
  const { data: day } = useGetReportSafetyQuery(reportId, { skip: !reportId });

  const num = (n: number) => fmtNum(n, locale);

  const stats: Stat[] = rollup
    ? [
        {
          label: t("hse.kpi.daysSinceLti"),
          value: num(rollup.days_since_lti_total),
          tone: "good",
        },
        {
          label: t("hse.kpi.daysSinceLtiOperator"),
          value: num(rollup.days_since_lti_operator),
          tone: "good",
        },
        { label: t("hse.kpi.observations"), value: num(rollup.observations_event), tone: "ops" },
        {
          label: t("hse.kpi.nearMisses"),
          value: num(rollup.near_misses_event),
          tone: rollup.near_misses_event > 0 ? "warn" : undefined,
        },
        { label: t("hse.kpi.drills"), value: num(rollup.drills_event), tone: "ops" },
      ]
    : [];

  return (
    <QueryState
      isLoading={rollupLoading}
      isError={rollupError}
      isEmpty={!rollup}
      emptyMessage={t("hse.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}

      {day ? <DayHeader day={day} /> : null}

      {/* daily HSE trail — one row per report day, newest first */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("hse.trail.title")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("hse.trail.date")}</th>
                <th className="num">{t("hse.trail.ridCards")}</th>
                <th className="num">{t("hse.trail.jsa")}</th>
                <th className="num">{t("hse.trail.toolbox")}</th>
                <th className="num">{t("hse.trail.drills")}</th>
                <th className="num">{t("hse.trail.recordables")}</th>
                <th className="num">{t("hse.trail.observations")}</th>
                <th>{t("hse.trail.bop")}</th>
              </tr>
            </thead>
            <tbody>
              {(rollup?.items ?? []).map((it) => (
                <tr
                  key={it.report_id}
                  data-owner="p"
                  className={it.report_id === reportId ? "row-selected" : undefined}
                >
                  <td className="tab-nums">{it.date_report}</td>
                  <td className="num tab-nums">{num(it.rid_cards)}</td>
                  <td className="num tab-nums">{num(it.jsa_count)}</td>
                  <td className="num tab-nums">{num(it.toolbox_talks)}</td>
                  <td className="num tab-nums">{num(it.drills)}</td>
                  <td className={`num tab-nums ${it.recordables > 0 ? "var-bad" : ""}`}>
                    {num(it.recordables)}
                  </td>
                  <td className="num tab-nums">{num(it.observations)}</td>
                  <td>
                    <BopBadge value={it.bop_test} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

/** The selected report's daily HSE header — grouped into three sections. */
function DayHeader({ day }: { day: SafetyDayOut }) {
  const { t, locale } = useLocale();
  const num = (n: number) => fmtNum(n, locale);
  const yesNo = (b: boolean) => (b ? t("hse.yes") : t("hse.no"));

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3>
        {t("hse.day.title")} <span className="tag-op">{day.date_report}</span>
      </h3>

      <h4 className="hse-subhead">{t("hse.day.wellControl")}</h4>
      <div className="kv-grid">
        <Kv k={t("hse.day.bopTest")}>
          <BopBadge value={day.bop_test} />
          {day.bop_test_time ? <span className="tab-nums"> · {day.bop_test_time}</span> : null}
        </Kv>
        <Kv k={t("hse.day.koomey")}>{yesNo(day.koomey_check)}</Kv>
      </div>

      <h4 className="hse-subhead">{t("hse.day.checks")}</h4>
      <div className="kv-grid">
        <KvNum k={t("hse.day.ridCards")} v={num(day.rid_cards)} />
        <KvNum k={t("hse.day.jsa")} v={num(day.jsa_count)} />
        <KvNum k={t("hse.day.toolbox")} v={num(day.toolbox_talks)} />
        <KvNum
          k={t("hse.day.drillsLogged")}
          v={
            day.drill_types.length
              ? `${num(day.drills)} · ${day.drill_types.join(", ")}`
              : num(day.drills)
          }
        />
      </div>

      <h4 className="hse-subhead">{t("hse.day.people")}</h4>
      <div className="kv-grid">
        <KvNum
          k={t("hse.day.crewOnSite")}
          v={day.crew_on_site != null ? num(day.crew_on_site) : t("common.na")}
        />
        <Kv k={t("hse.day.recordables")}>
          <span className={day.recordables > 0 ? "var-bad" : undefined}>
            {num(day.recordables)}
          </span>
        </Kv>
        <KvNum k={t("hse.day.firstAids")} v={num(day.first_aids)} />
        <Kv k={t("hse.kpi.nearMisses")}>
          <span className={day.near_misses > 0 ? "var-bad" : undefined}>
            {num(day.near_misses)}
          </span>
        </Kv>
        <KvNum k={t("hse.kpi.observations")} v={num(day.observations)} />
      </div>

      {day.narrative ? (
        <>
          <h4 className="hse-subhead">{t("hse.day.narrative")}</h4>
          <p className="hint">{day.narrative}</p>
        </>
      ) : null}
    </div>
  );
}

function Kv({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className="kv-v">{children}</span>
    </div>
  );
}

function KvNum({ k, v }: { k: string; v: string }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className="kv-v mono tab-nums">{v}</span>
    </div>
  );
}

/** BOP-test result badge. Absent / N/A shows a muted dash. */
function BopBadge({ value }: { value: SafetyDayOut["bop_test"] }) {
  const { t } = useLocale();
  if (value === "pass") return <span className="badge var-good">{t("hse.bop.pass")}</span>;
  if (value === "fail") return <span className="badge var-bad">{t("hse.bop.fail")}</span>;
  return <span className="badge">{t("hse.bop.na")}</span>;
}
